import ArrowDownIcon from '@assets/icons/arrow-long-down.svg';
import ArrowUpIcon from '@assets/icons/arrow-long-up.svg';
import React, { useRef, useState } from 'react';
import { Animated, Platform, Pressable, View, ViewStyle } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { getLeftRightValues } from '@experiences/components/table/blocs/table';
import { SortDirection } from '@tanstack/react-table';
import { useTableContext } from './blocs/table-context';
export interface TableHeaderCellProps {
  children: React.ReactNode;
  columnId: string;
  onPress?: () => void;
  pinnedOffset?: number;
  sortDirection?: SortDirection;
  style?: ViewStyle;
}

export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  children,
  onPress,
  columnId,
  pinnedOffset,
  sortDirection,
  style,
}) => {
  const [styles, theme] = useStyles();
  const [isHovered, setIsHovered] = useState(false);
  const [hasSortedThisHover, setHasSortedThisHover] = useState(false);
  const pressableRef = useRef<React.ComponentRef<typeof Pressable>>(null);
  const { columnWidths, headerPadding, pinnedScrollOffset } = useTableContext();

  const [paddingLeft, paddingRight] = getLeftRightValues(
    headerPadding ?? theme.sk.spacing.spaceMedium,
  );

  const handleHoverIn = () => setIsHovered(true);
  const handleHoverOut = () => {
    setIsHovered(false);
    setHasSortedThisHover(false);
  };
  const handlePress = () => {
    setHasSortedThisHover(true);
    onPress?.();
  };

  const columnWidth = columnWidths[columnId];
  const headerCellStyles = [
    styles.container,
    { paddingLeft, paddingRight, width: columnWidth },
    ...(typeof pinnedOffset === 'number'
      ? [
          styles.pinned,
          ...(Platform.OS === 'web' ? [{ left: pinnedOffset }] : []),
        ]
      : []),
    style,
  ];
  const hoveredStyle = isHovered ? styles.hovered : {};
  const iconFill = isHovered
    ? theme.sk.color.icon.secondary
    : theme.sk.color.icon.tertiary;

  const isPinnedNative =
    typeof pinnedOffset === 'number' &&
    pinnedScrollOffset &&
    Platform.OS !== 'web';
  const pinnedTransform = isPinnedNative
    ? [
        styles.pinnedWrapper,
        { transform: [{ translateX: pinnedScrollOffset }] },
      ]
    : undefined;

  const ariaSort =
    sortDirection === 'asc'
      ? 'ascending'
      : sortDirection === 'desc'
        ? 'descending'
        : 'none';

  const content = onPress ? (
    <Pressable
      aria-sort={ariaSort}
      onHoverIn={handleHoverIn}
      onHoverOut={handleHoverOut}
      onPress={handlePress}
      ref={pressableRef}
      role="columnheader"
      style={[headerCellStyles, hoveredStyle]}
    >
      <View style={styles.contentContainer}>
        {children}
        <View style={styles.iconContainer}>
          {sortDirection === 'asc' ? (
            <ArrowUpIcon fill={iconFill} height={12} width={12} />
          ) : sortDirection === 'desc' ? (
            <ArrowDownIcon fill={iconFill} height={12} width={12} />
          ) : isHovered && !hasSortedThisHover ? (
            // Show hint icon on hover (unless user has cycled back to no-sort)
            <ArrowUpIcon
              fill={theme.sk.color.icon.secondary}
              height={12}
              width={12}
            />
          ) : null}
        </View>
      </View>
    </Pressable>
  ) : (
    <View role="columnheader" style={headerCellStyles}>
      {children}
    </View>
  );

  // On native, wrap pinned header cells in Animated.View with translateX
  // to keep them fixed during horizontal scrolling
  if (isPinnedNative) {
    return <Animated.View style={pinnedTransform}>{content}</Animated.View>;
  }

  return content;
};

const useStyles = createStyles((theme) => ({
  container: {
    backgroundColor: theme.sk.color.bg.primary.default,
    justifyContent: 'center',
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  contentContainer: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  hovered: {
    backgroundColor: theme.sk.color.bg.primary.hover,
  },
  iconContainer: {
    minWidth: 12,
  },
  pinned: {
    ...Platform.select({
      web: { position: 'sticky' as ViewStyle['position'] },
    }),
    zIndex: 1,
  },
  pinnedWrapper: {
    flexDirection: 'row',
    zIndex: 1,
  },
}));
